import { Link, useNavigate, useSearchParams } from "react-router-dom"
import {
  BookOpen,
  Camera,
  ChevronLeft,
  MessageCircle,
  NotebookPen,
  Plus,
} from "lucide-react"
import { useProfile } from "~/hooks/useProfile"
import { useEffect, useMemo, useState } from "react"
import { fetchStory, type StoryEntry } from "~/api/story"
import { EmptyState } from "~/components/EmptyState"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"
import { ENTITLEMENTS, hasEntitlement } from "~/utils/entitlements"

type StoryFilter = "all" | "observation" | "conversation" | "photo" | "note"

const TYPE_COLORS: Record<string, string> = {
  milestone: "bg-amber-500",
  observation: "bg-emerald-500",
  conversation: "bg-sky-500",
  photo: "bg-violet-500",
  note: "bg-rose-500",
  perspective: "bg-orange-500",
  idea: "bg-teal-500",
}

function monthKey(iso: string): string {
  const date = new Date(iso)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
}

function monthLabel(key: string): string {
  const [year, month] = key.split("-").map(Number)
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  })
}

function previewText(entry: StoryEntry): string {
  const note = entry.user_note?.trim()
  if (note && !note.startsWith("/images/") && !note.includes("/rails/active_storage/")) {
    return note
  }
  const body = entry.body?.trim()
  if (!body) return ""
  return body.length > 140 ? `${body.slice(0, 137)}…` : body
}

function plusCta(label: string) {
  return (
    <Link
      to="/plus"
      className="rounded-lg bg-[var(--color-brand-primary)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
    >
      {label}
    </Link>
  )
}

export function StoryPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { profile } = useProfile()
  const [entries, setEntries] = useState<StoryEntry[]>([])
  const [storyWritable, setStoryWritable] = useState(false)
  const [apiReadOnly, setApiReadOnly] = useState(false)
  const [entriesLoadFailed, setEntriesLoadFailed] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [filter, setFilter] = useState<StoryFilter>("all")
  const [storyDogName, setStoryDogName] = useState<string | null>(null)
  const [storyDogId, setStoryDogId] = useState<number | undefined>(undefined)

  const queryDogId = useMemo(() => {
    const raw = searchParams.get("dog_id")
    if (!raw) return undefined
    const id = Number(raw)
    return Number.isFinite(id) ? id : undefined
  }, [searchParams])

  const accountHasJournal = hasEntitlement(profile?.user.entitlements, ENTITLEMENTS.dogJournal)
  // Account-level Plus must unlock Story for every dog, even if a Story fetch fails.
  const canWrite = accountHasJournal || storyWritable
  const showExpiredBanner = loaded && !accountHasJournal && apiReadOnly

  const dogName =
    storyDogName ??
    profile?.dogs?.find((dog) => dog.id === queryDogId)?.name ??
    profile?.dog_profile?.name ??
    "your dog"

  useEffect(() => {
    setLoaded(false)
    setEntriesLoadFailed(false)
    setStoryWritable(false)
    setApiReadOnly(false)
    fetchStory(queryDogId)
      .then((res) => {
        setEntries(res.entries)
        setStoryWritable(Boolean(res.has_entitlement) && !res.read_only)
        setApiReadOnly(Boolean(res.read_only))
        setStoryDogName(res.dog_name ?? null)
        setStoryDogId(res.dog_id ?? queryDogId)
      })
      .catch((err) => {
        console.error(err)
        setEntries([])
        setEntriesLoadFailed(true)
      })
      .finally(() => setLoaded(true))
  }, [queryDogId])

  const photoEntries = entries.filter((entry) => entry.entry_type === "photo")
  const filtered = useMemo(() => {
    if (filter === "all") return entries
    if (filter === "photo") return photoEntries
    return entries.filter((entry) => entry.entry_type === filter)
  }, [entries, filter, photoEntries])

  const grouped = useMemo(() => {
    const groups: { key: string; label: string; entries: StoryEntry[] }[] = []
    const byMonth = new Map<string, StoryEntry[]>()
    filtered.forEach((entry) => {
      const key = monthKey(entry.occurred_at)
      const list = byMonth.get(key) ?? []
      list.push(entry)
      byMonth.set(key, list)
    })
    Array.from(byMonth.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .forEach(([key, monthEntries]) => {
        groups.push({ key, label: monthLabel(key), entries: monthEntries })
      })
    return groups
  }, [filtered])

  const memoryPath = (type?: string) => {
    const params = new URLSearchParams()
    if (storyDogId) params.set("dog_id", String(storyDogId))
    if (type) params.set("type", type)
    const query = params.toString()
    return query ? `/story/new?${query}` : "/story/new"
  }
  const newMemoryPath = memoryPath()
  const detailPath = (entryId: number) =>
    storyDogId ? `/story/${entryId}?dog_id=${storyDogId}` : `/story/${entryId}`

  function emptyStateForFilter() {
    if (filter === "all") {
      return (
        <EmptyState
          icon={<BookOpen size={48} className="text-[var(--color-text-secondary)]" />}
          title="Every great friendship has a beginning."
          description={
            canWrite
              ? `Save your first memory to start ${dogName}’s Story.`
              : `${dogName}’s Story is available with Doggy Dynamo Plus. Existing memories stay safe if you reactivate.`
          }
          action={
            canWrite ? (
              <Link
                to={newMemoryPath}
                className="rounded-lg bg-[var(--color-brand-primary)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                Add First Memory
              </Link>
            ) : (
              plusCta("Discover Plus")
            )
          }
        />
      )
    }
    if (filter === "observation") {
      return (
        <EmptyState
          icon={<NotebookPen size={40} className="text-[var(--color-text-secondary)]" />}
          title="No observations yet"
          description={`Start keeping track of the little things you notice about ${dogName}. Over time they’ll help tell your story.`}
          action={
            canWrite ? (
              <Link
                to={memoryPath("observation")}
                className="rounded-lg bg-[var(--color-brand-primary)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                Add Observation
              </Link>
            ) : (
              plusCta("Discover Plus")
            )
          }
        />
      )
    }
    if (filter === "conversation") {
      return (
        <EmptyState
          icon={<MessageCircle size={40} className="text-[var(--color-text-secondary)]" />}
          title="No saved conversations yet"
          description={`Save important Ask Dynamo conversations to ${dogName}'s Story so you can revisit them anytime.`}
          action={
            canWrite ? (
              <Link
                to="/ask-dynamo"
                className="rounded-lg bg-[var(--color-brand-primary)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                Ask Dynamo
              </Link>
            ) : (
              plusCta("Discover Plus")
            )
          }
        />
      )
    }
    if (filter === "note") {
      return (
        <EmptyState
          icon={<NotebookPen size={40} className="text-[var(--color-text-secondary)]" />}
          title="No notes yet"
          description={`Jot down anything you want to remember about ${dogName}.`}
          action={
            canWrite ? (
              <Link
                to={memoryPath("note")}
                className="rounded-lg bg-[var(--color-brand-primary)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                Add Note
              </Link>
            ) : (
              plusCta("Discover Plus")
            )
          }
        />
      )
    }
    return (
      <EmptyState
        icon={<Camera size={40} className="text-[var(--color-text-secondary)]" />}
        title="No Story photos yet"
        description={`Add your favourite moments so they’ll always be part of ${dogName}’s Story.`}
        action={
          canWrite ? (
            <Link
              to={memoryPath("photo")}
              className="rounded-lg bg-[var(--color-brand-primary)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              Add Photo
            </Link>
          ) : (
            plusCta("Discover Plus")
          )
        }
      />
    )
  }

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
      <header className="mb-4 flex items-center gap-4">
          <Link
            to="/perspectives"
            aria-label="Back to Dogs"
            className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
          >
            <ChevronLeft size={20} aria-hidden />
          </Link>
          <h1 className="flex-1 text-lg font-semibold text-[var(--color-text-primary)]">
            {dogName}&apos;s Story
          </h1>
          {canWrite && (
            <Link
              to={newMemoryPath}
              className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-brand-primary)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              <Plus size={16} aria-hidden />
              Add Memory
            </Link>
          )}
      </header>

      <div>
        {loaded && entriesLoadFailed && (
          <div
            className="mb-4 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4 text-sm text-[var(--color-text-secondary)]"
            role="alert"
          >
            <p className="font-medium text-[var(--color-text-primary)]">Couldn’t load memories</p>
            <p className="mt-1">
              Something went wrong loading {dogName}’s Story.{" "}
              {canWrite ? "You can still add a new memory." : "Please try again in a moment."}
            </p>
          </div>
        )}

        {showExpiredBanner && (
          <div
            className="mb-4 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4 text-sm text-[var(--color-text-secondary)]"
            role="status"
          >
            <p className="font-medium text-[var(--color-text-primary)]">Your Story is Safe</p>
            <p className="mt-1">
              Your Doggy Dynamo Plus membership has ended. You can still enjoy everything you’ve
              saved, but you’ll need to restart Plus to add new memories.
            </p>
            <Link
              to="/plus"
              className="mt-2 inline-block font-medium text-[var(--color-brand-primary)] underline"
            >
              Restart Doggy Dynamo Plus
            </Link>
          </div>
        )}

        {loaded && (
          <>
            <div className="mb-4 flex flex-wrap gap-2">
              {(
                [
                  ["all", "All"],
                  ["observation", "Observations"],
                  ["conversation", "Conversations"],
                  ["photo", "Photos"],
                  ["note", "Notes"],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFilter(key)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium ${
                    filter === key
                      ? "border-[var(--color-brand-primary)] bg-[var(--color-brand-primary)] text-white"
                      : "border-[var(--color-divider)] text-[var(--color-text-secondary)]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {filtered.length === 0 ? (
              emptyStateForFilter()
            ) : (
              <div className="relative space-y-8 pl-2">
                {grouped.map((group) => (
                  <section key={group.key} aria-labelledby={`story-month-${group.key}`}>
                    <h2
                      id={`story-month-${group.key}`}
                      className="mb-4 text-sm font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]"
                    >
                      {group.label}
                    </h2>
                    <ol className="relative space-y-4 border-l-2 border-[var(--color-divider)] pl-5">
                      {group.entries.map((entry) => (
                        <li key={entry.id} className="relative">
                          <span
                            className={`absolute -left-[1.55rem] top-3 h-3 w-3 rounded-full ring-4 ring-[var(--color-surface-main)] ${
                              TYPE_COLORS[entry.entry_type] ?? "bg-[var(--color-brand-primary)]"
                            }`}
                            aria-hidden
                          />
                          <button
                            type="button"
                            onClick={() => navigate(detailPath(entry.id))}
                            className="w-full rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4 text-left transition hover:border-[var(--color-brand-primary)]/40"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex min-w-0 flex-1 items-start gap-3">
                                {entry.entry_type === "photo" && entry.image_url && (
                                  <img
                                    src={entry.image_url}
                                    alt=""
                                    className="h-14 w-14 shrink-0 rounded-lg object-cover"
                                  />
                                )}
                                <div className="min-w-0">
                                  <p className="text-xs uppercase tracking-wide text-[var(--color-text-tertiary)]">
                                    {entry.entry_type}
                                  </p>
                                  <h3 className="font-medium text-[var(--color-text-primary)]">
                                    {entry.title}
                                  </h3>
                                </div>
                              </div>
                              <time className="shrink-0 text-xs text-[var(--color-text-tertiary)]">
                                {new Date(entry.occurred_at).toLocaleDateString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                })}
                              </time>
                            </div>
                            {previewText(entry) && (
                              <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
                                {previewText(entry)}
                              </p>
                            )}
                          </button>
                        </li>
                      ))}
                    </ol>
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      </LoggedInScreenCard>
    </PageShell>
  )
}
